import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import {
  GridIcon,
  ChevronDownIcon,
  HorizontaLDots,
  ListIcon,
  TableIcon,
  BoxIcon,
  BoxIconLine,
  BoxCubeIcon,
  PageIcon,
  DollarLineIcon,
  GroupIcon,
} from "../icons";
import { useSidebar } from "../context/SidebarContext";
import { useAuthStore } from "../store/useAuthStore";
import { useDailyReportMode } from "../hooks/useFarmFeatures";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string }[];
};

const navItems: NavItem[] = [
  {
    icon: <GridIcon />,
    name: "Dashboard",
    path: "/",
  },
  {
    name: "Daily Entry",
    icon: <ListIcon />,
    subItems: [
      { name: "Feed Distribution", path: "/feed" },
      { name: "Egg Collection", path: "/eggs" },
      { name: "Mortality", path: "/mortality" },
      { name: "Measurements", path: "/flock-metrics" },
    ],
  },
  {
    icon: <BoxIconLine />,
    name: "Purchases",
    path: "/purchases",
  },
  {
    name: "Inventory",
    icon: <BoxIcon />,
    subItems: [
      { name: "Flocks", path: "/flocks" },
      { name: "Stock Balances", path: "/stock" },
      { name: "Feed Production", path: "/feed-production" },
    ],
  },
  {
    name: "Sales",
    icon: <BoxCubeIcon />,
    subItems: [
      { name: "Egg Sales", path: "/egg-sales" },
      { name: "Bird Sales", path: "/bird-sales" },
      { name: "Manure Sales", path: "/manure-sales" },
      { name: "Bag Sales", path: "/bag-sales" },
    ],
  },
  {
    name: "Labour",
    icon: <GroupIcon />,
    subItems: [
      { name: "Attendance", path: "/labour/attendance" },
      { name: "People", path: "/labour/people" },
      { name: "Payroll", path: "/labour/payroll" },
    ],
  },
  {
    name: "Assets",
    icon: <BoxCubeIcon />,
    subItems: [
      { name: "Maintenance", path: "/assets/maintenance" },
      { name: "Register", path: "/assets/register" },
    ],
  },
  {
    name: "Finance",
    icon: <DollarLineIcon />,
    subItems: [
      { name: "Cockpit", path: "/finance/cockpit" },
      { name: "Collections", path: "/finance/collections" },
      { name: "Payments due", path: "/finance/payments-due" },
      { name: "Vouchers", path: "/finance/vouchers" },
      { name: "General ledger", path: "/finance/general-ledger" },
      { name: "Subsidies", path: "/finance/subsidies" },
      { name: "Loans & bank", path: "/finance/loans" },
      { name: "Cash flow & forecast", path: "/finance/cash-flow" },
      { name: "Chart of accounts", path: "/finance/chart-of-accounts" },
      { name: "Trial balance", path: "/finance/trial-balance" },
      { name: "Profit & loss", path: "/finance/profit-and-loss" },
      { name: "Balance sheet", path: "/finance/balance-sheet" },
      { name: "Opening balances", path: "/finance/opening-balances" },
      { name: "NECC rates", path: "/finance/necc-rates" },
      { name: "Egg price realisation", path: "/finance/price-realisation" },
      { name: "Farm P&L", path: "/farm-pnl" },
      { name: "Flock Economics", path: "/flock-economics" },
      { name: "Expenses", path: "/expenses" },
      { name: "Accounts Payable", path: "/payables" },
      { name: "Accounts Receivable", path: "/receivables" },
      // One Bank screen: accounts + uploading. Statements are listed inside
      // each account (the separate Statement Uploads page went, 2026-10).
      { name: "Bank", path: "/finance/bank-accounts" },
    ],
  },
  {
    name: "Master Data",
    icon: <TableIcon />,
    subItems: [
      { name: "Sheds", path: "/sheds" },
      { name: "Raw Materials", path: "/raw-materials" },
      { name: "Suppliers", path: "/suppliers" },
      { name: "Feed Formulas", path: "/feed-formulas" },
      { name: "Buyers", path: "/buyers" },
      { name: "Breeds", path: "/breeds" },
      { name: "Breed Standards", path: "/standard-curves" },
      { name: "Vaccination", path: "/vaccination-schedule" },
      { name: "Health", path: "/flock-health" },
      { name: "Expense Categories", path: "/expense-categories" },
    ],
  },
];

const othersItems: NavItem[] = [
  {
    icon: <BoxCubeIcon />,
    name: "Reports",
    subItems: [
      { name: "CSV Exports", path: "/reports" },
      { name: "Production Report", path: "/production-report" },
      { name: "Egg Stock", path: "/egg-stock-report" },
      { name: "Egg Grading", path: "/egg-grading" },
      { name: "Feed Inventory", path: "/feed-inventory-report" },
    ],
  },
  {
    icon: <PageIcon />,
    name: "Admin",
    subItems: [
      { name: "Users", path: "/users" },
      { name: "Activity Log", path: "/activity-log" },
      { name: "QR Code", path: "/qr-profile" },
    ],
  },
];

// Who sees each Reports link. Must match the route's ProtectedRoute in
// App.tsx (and so the API behind it); the page sweep checks that a shown link
// opens. /production-report's route admits every role — the link is just not
// offered to a supervisor, whose sidebar has no Reports group.
const REPORT_PATH_ROLES: Record<string, string[]> = {
  // /api/reports/* is owner + viewer.
  "/reports": ["owner", "viewer"],
  "/egg-stock-report": ["owner", "viewer"],
  "/feed-inventory-report": ["owner", "viewer"],
  "/production-report": ["owner", "viewer", "manager"],
  // A manager sells the eggs and acts on a worsening mix.
  "/egg-grading": ["owner", "manager"],
};

const systemAdminNavItems: NavItem[] = [
  {
    icon: <GridIcon />,
    name: "Farms",
    path: "/system",
  },
];

const dailyReportNavItem: NavItem = {
  icon: <PageIcon />,
  name: "Daily Report",
  path: "/daily-report",
};

// Sales is opportunistic (buyer-driven) — manager needs direct
// access when a buyer arrives, separate from the once-a-day Daily
// Report cadence. Supervisor's wizard has no Sales step, so the
// shortcut is manager-only.
const managerSalesNavItem: NavItem = {
  name: "Sales",
  icon: <BoxCubeIcon />,
  subItems: [
    { name: "Egg Sales", path: "/egg-sales" },
    { name: "Bird Sales", path: "/bird-sales" },
    { name: "Manure Sales", path: "/manure-sales" },
    { name: "Bag Sales", path: "/bag-sales" },
  ],
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered, toggleMobileSidebar } = useSidebar();
  const location = useLocation();
  const userRole = useAuthStore((s) => s.user?.role);
  const isSystemAdmin = userRole === "admin";
  const isDailyReportMode = useDailyReportMode();

  // Close mobile sidebar on navigation
  useEffect(() => {
    if (isMobileOpen) {
      toggleMobileSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Role-based sidebar filtering for the "others" menu: a link is never shown
  // to a role its page route refuses, or the click bounces to the dashboard
  // (the page sweep found Egg Grading shown to a viewer who can't open it, and
  // hidden from the manager who can — docs/E2E_JOURNEY_PLAN.md F1). A group
  // with no links left is dropped. Supervisor sees neither group.
  const filteredOthersItems = useMemo(() => {
    return othersItems.flatMap((item) => {
      if (item.name === "Admin") return userRole === "owner" ? [item] : [];
      if (item.name === "Reports") {
        const subItems = (item.subItems ?? []).filter(
          (sub) => !!userRole && (REPORT_PATH_ROLES[sub.path] ?? []).includes(userRole),
        );
        return subItems.length ? [{ ...item, subItems }] : [];
      }
      return [item];
    });
  }, [userRole]);

  // Owner-only sub-items inside the main nav (e.g. Bank Statements).
  const filteredNavItems = useMemo(() => {
    const ownerOnlyPaths = new Set([
      "/finance/cockpit",
      // OWNER ONLY, unlike Collections one line down in the menu. Chasing a
      // payment is a job a manager does; deciding which supplier to pay out of
      // a finite bank balance is treasury, and payables writes are owner-gated
      // already. Widening it would be a new exposure, not a symmetry.
      "/finance/payments-due",
      // The chart decides where every figure on the statements below can
      // land, so it is gated with them.
      // A voucher decides what every statement below says.
      // The register carries wages, so it is owner-only. Attendance is NOT
      // in this list on purpose: whoever walks the sheds marks it.
      "/labour/people",
      "/labour/payroll",
      // The register carries cost, rate and written-down value — figures a
      // CA signs. Maintenance is NOT owner-gated: whoever changes the oil
      // has to see it is due.
      "/assets/register",
      "/finance/vouchers",
      "/finance/general-ledger",
      "/finance/subsidies",
      // Sanctioned limits, drawing power and what the farm owes are the
      // figures a bank and a CA read.
      "/finance/loans",
      // A forecast names what the partners draw and the month the farm runs
      // out of money.
      "/finance/cash-flow",
      "/finance/chart-of-accounts",
      "/finance/trial-balance",
      "/finance/profit-and-loss",
      "/finance/balance-sheet",
      // Owner-only for the same reason as the statements above: these figures
      // decide what every one of them says.
      "/finance/opening-balances",
      // The realisation REPORT is owner-only like the other whole-farm money
      // views: it names buyers and totals what each one costs.
      "/finance/price-realisation",
      "/finance/bank-accounts",
      "/finance/bank-statements",
    ]);
    // Economics pages the backend gates to owner+viewer (RequireRole owner,viewer):
    // hide from manager/supervisor so they don't hit a 403 from a visible menu.
    const ownerViewerPaths = new Set(["/farm-pnl", "/flock-economics"]);
    // Entering the declared NECC rate is owner OR MANAGER on the backend
    // (RequireRole "owner","manager"). Gating the link to owners would put the
    // page out of reach of half the people allowed to fill it in — the same
    // shape of mistake as shipping the measurements form where no supervisor
    // could see it.
    // Collections is owner OR MANAGER for the same reason, and one more:
    // chasing a payment is a job somebody does rather than a report somebody
    // reads, and the manager is the one doing it. It is not a new exposure —
    // a manager already sees a buyer's outstanding and overdue at the till,
    // through <BuyerCreditNotice> on the Daily Report sales step.
    const ownerManagerPaths = new Set([
      "/finance/necc-rates",
      "/finance/collections",
    ]);
    return navItems.map((item) => {
      if (!item.subItems) return item;
      return {
        ...item,
        subItems: item.subItems.filter((sub) => {
          if (ownerOnlyPaths.has(sub.path)) return userRole === "owner";
          if (ownerViewerPaths.has(sub.path)) return userRole === "owner" || userRole === "viewer";
          if (ownerManagerPaths.has(sub.path)) return userRole === "owner" || userRole === "manager";
          return true;
        }),
      };
    });
  }, [userRole]);

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/";
      return location.pathname === path || location.pathname.startsWith(path + "/");
    },
    [location.pathname]
  );

  // The "main" menu rendered changes by role/mode — system-admin sees
  // systemAdminNavItems, manager/supervisor in daily_report_mode see
  // the daily-report list (manager also gets a Sales shortcut),
  // everyone else sees filteredNavItems. The auto-open scan below
  // MUST mirror the same selection or the indices it produces (used
  // as keys for the open submenu + height map) point at the wrong
  // rendered group and submenus stay collapsed on direct navigation
  // / page reload.
  const dailyReportNavItems = useMemo<NavItem[]>(
    () =>
      userRole === "manager"
        ? [dailyReportNavItem, managerSalesNavItem]
        : [dailyReportNavItem],
    [userRole],
  );
  const mainNavItems = isSystemAdmin
    ? systemAdminNavItems
    : isDailyReportMode
      ? dailyReportNavItems
      : filteredNavItems;

  useEffect(() => {
    let submenuMatched = false;
    (["main", "others"] as const).forEach((menuType) => {
      // The same lists the menu renders, so the indices name the same groups.
      const items = menuType === "main" ? mainNavItems : filteredOthersItems;
      items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({ type: menuType, index });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, mainNavItems, filteredOthersItems]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null;
      }
      return { type: menuType, index };
    });
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className="flex flex-col gap-4">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? "menu-item-active"
                  : "menu-item-inactive"
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? "lg:justify-center"
                  : "lg:justify-start"
              }`}
            >
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-icon-active"
                    : "menu-item-icon-inactive"
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="menu-item-text">{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? "menu-item-arrow-active"
                      : ""
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                className={`menu-item group ${
                  isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${menuType}-${index}`] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : "0px",
              }}
            >
              <ul className="mt-2 space-y-1 ml-9">
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path)
                          ? "menu-dropdown-item-active"
                          : "menu-dropdown-item-inactive"
                      }`}
                    >
                      {subItem.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <aside
      className={`sidebar-surface fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 text-white h-screen transition-all duration-300 ease-in-out z-50 border-r border-white/10
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-8 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to={isSystemAdmin ? "/system" : "/"}>
          {isExpanded || isHovered || isMobileOpen ? (
            <span className="font-display text-xl font-semibold tracking-tight text-white">
              Poultry Book
            </span>
          ) : (
            <span className="font-display text-xl font-semibold tracking-tight text-white">
              PB
            </span>
          )}
        </Link>
      </div>
      <div className="flex flex-col overflow-y-auto overscroll-contain duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-4">
            {isSystemAdmin ? (
              <div>
                <h2
                  className={`mb-4 text-xs uppercase flex leading-[20px] text-brand-200/70 ${
                    !isExpanded && !isHovered
                      ? "lg:justify-center"
                      : "justify-start"
                  }`}
                >
                  {isExpanded || isHovered || isMobileOpen ? (
                    "System"
                  ) : (
                    <HorizontaLDots className="size-6" />
                  )}
                </h2>
                {renderMenuItems(systemAdminNavItems, "main")}
              </div>
            ) : isDailyReportMode ? (
              <div>
                <h2
                  className={`mb-4 text-xs uppercase flex leading-[20px] text-brand-200/70 ${
                    !isExpanded && !isHovered
                      ? "lg:justify-center"
                      : "justify-start"
                  }`}
                >
                  {isExpanded || isHovered || isMobileOpen ? (
                    "Daily Report"
                  ) : (
                    <HorizontaLDots className="size-6" />
                  )}
                </h2>
                {renderMenuItems(mainNavItems, "main")}
              </div>
            ) : (
              <>
                <div>
                  <h2
                    className={`mb-4 text-xs uppercase flex leading-[20px] text-brand-200/70 ${
                      !isExpanded && !isHovered
                        ? "lg:justify-center"
                        : "justify-start"
                    }`}
                  >
                    {isExpanded || isHovered || isMobileOpen ? (
                      "Menu"
                    ) : (
                      <HorizontaLDots className="size-6" />
                    )}
                  </h2>
                  {renderMenuItems(filteredNavItems, "main")}
                </div>
                <div>
                  <h2
                    className={`mb-4 text-xs uppercase flex leading-[20px] text-brand-200/70 ${
                      !isExpanded && !isHovered
                        ? "lg:justify-center"
                        : "justify-start"
                    }`}
                  >
                    {isExpanded || isHovered || isMobileOpen ? (
                      "Others"
                    ) : (
                      <HorizontaLDots />
                    )}
                  </h2>
                  {renderMenuItems(filteredOthersItems, "others")}
                </div>
              </>
            )}
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;
